import { Injectable, OnDestroy } from '@angular/core';
import { XsI18nPipe } from '@nx-fe/pipes';
import { FeatureToggleService, GlobalEventBusService } from '@nx-fe/services';
import { GlobalEvents } from 'app/core/global-event-constants';
import { ToastTypeIndex, Toast } from 'app/shared/components/toast/toast.component';
import { ShortDateFormatPipe } from 'app/shared/pipes/time-format.pipe';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  IFileActionProgress,
  ContentStatus,
  IFileManagerActionItem,
  IFileInfo,
  FileData,
  ActionType,
} from '../file-manager/fileData';
import { FileManagerState } from '../file-manager/FileManagerState';
import { DeviceIdComparerService } from './device-id-comparer.service';
import { FileSystemErrorCodes, OsTypes } from '../../remote-control-ng/definitions';
import { FileDownloadService } from '../../remote-control-ng/services/downloadService';
import { RcFileSystemService } from '../../remote-control-ng/context/fileSystem';
import { RcContext } from '../../remote-control-ng/context/rcContext';
import { OsDetectionService } from '../../remote-control-ng/services/osDetectionService';
import { getWin32ErrorMessage } from 'app/modules/remote-control-ng';

export interface UploadEvent {
  content: UploadContent[];
  reason?: FileCheckType;
}

export interface UploadContent {
  content: File;
  path: string;
  overwrite?: boolean;
}

export interface FileMetaData {
  FileName: string;
  FileType: string;
  Content: string;
  IsNew: boolean;
  IsDeleted: boolean;
  isError: boolean;
  reason?: string;
}

export enum FileCheckType {
  MaxFileSize,
  MinFileSize,
  FileType,
  FileCorrupt,
  FileResolution,
}

class DeviceIdToFileExplorerStateMap {
  [deviceId: string]: FileManagerState;
}

export enum AgentErrorCode {
  Not_Supported = -1,
  Success = 0,
  Not_Exists = 2,
  Access_Denied = 5,
  Invalid_Param = 87,
  Disk_Full = 112,
  Folder_Not_Empty = 145,
  Folder_Empty = 150,
  Already_Exists = 183,
  Client_Upload_Cancelled_Request = 190,
  Client_Download_Cancelled_Request = 191,
  OperationCancelled = 1223,
}

@Injectable()
export class FileManagerService implements OnDestroy {
  /** Status Center Events data Map */
  public statusCenterData: Map<Object, IFileActionProgress>;
  /** Holds the status of unseen failures occurred during file operations*/
  public isFieldIndicatorActive = false;
  /** Emitted when the RC session ends */
  public sessionEnded: Subject<void>;
  /** Raises the event when partially uploaded or downloaded file is deleted */
  public fileActionCancelled: Subject<void>;
  
  private readonly progressDone = 100;
  private states: DeviceIdToFileExplorerStateMap;

  // Queue Upload and Download commands
  private commandsQueue: Array<IFileManagerActionItem> = [];
  private commandInProgress: boolean;
  private zipFilesQueue: Array<string> = [];

  // File Upload
  private deviceKind: string;
  private uploadFileList: UploadContent[] = [];
  private currentUploadFile: UploadContent;
  private uploadFilePath: string;
  private uploadDirectoryIndex = -1;
  private uploadDirectoryPath: string;
  private isRcSessionClosing: boolean;
  // Status Center Subscription
  private fileUploadProgressSubscription: Subscription;
  private fileDownloadProgressSubscription: Subscription;
  private fileDownloadTriggerSubscription: Subscription;
  private itemCompressTriggerSubscription: Subscription;
  private itemCompressAndDownloadTriggerSubscription: Subscription;
  private onDestroy = new Subject<void>();
  public queuePaused = false;

  constructor(
    private saI18nPipe: XsI18nPipe,
    private dateFormatPipe: ShortDateFormatPipe,
    private featureToggleService: FeatureToggleService,
    private globalEventBusService: GlobalEventBusService,
    private deviceIdComparerService: DeviceIdComparerService,
    private downloadService: FileDownloadService,
    private fileSystem: RcFileSystemService,
  ) {
    this.states = new DeviceIdToFileExplorerStateMap();
    this.sessionEnded = new Subject<void>();
    this.fileActionCancelled = new Subject<void>();
    this.statusCenterData = new Map<Object, IFileActionProgress>();
    this.isCommandInProgress = false;

    this.fileUploadProgressSubscription = this.globalEventBusService.register(
      GlobalEvents.fileUploadUpdated,
      (result: IFileActionProgress) => {
        if (result) {
          if (!this.statusCenterData.get(result.file)) {
            this.statusCenterData.set(result.file, result);
          } else if (result.contentStatus === ContentStatus.InProgress) {
            this.statusCenterData.get(result.file).progress = result.progress;
          } else {
            this.statusCenterData.set(result.file, result);
          }
        } else {
          console.error('Error: There is some error in getting upload file progress info. Please try again.');
        }
      }
    );

    this.fileDownloadProgressSubscription = this.globalEventBusService.register(
      GlobalEvents.fileDownloadUpdated,
      (result: IFileActionProgress) => {
        if (result) {
          const key = (result.actionType !== ActionType.Compress ? result.location.concat(result.file.name) : result.location).concat(result.file.lastModified.toString());
          if (!this.statusCenterData.get(key)) {
            this.statusCenterData.set(key, result);
          } else if (result.contentStatus === ContentStatus.InProgress) {
            this.statusCenterData.get(key).progress = result.progress;
          } else {
            this.statusCenterData.delete(key);
            this.statusCenterData.set(key, result);
          }
        } else {
          console.error('Error: There is some error in getting download file progress info. Please try again.');
        }
      }
    );

    this.fileDownloadTriggerSubscription = this.globalEventBusService.register(
      GlobalEvents.fileDownloadTriggered,
      (fileInfo: IFileInfo) => {
        if (fileInfo) {
          const toastMessage = this.saI18nPipe.transform(
            'file-manager-status-center-notification-download-added-to-the-queue',
            'file',
            fileInfo.fileName
          );
          this.displayToastMessage(toastMessage, ToastTypeIndex.info);
          this.downloadContent(fileInfo.fileName, fileInfo.filePath);
        }
      }
    );

    this.itemCompressTriggerSubscription = this.globalEventBusService.register(
      GlobalEvents.ItemCompressTriggered,
      (result: IFileActionProgress) => {
        if (result) {
          const toastMessage = this.saI18nPipe.transform(
            'file-manager-status-center-notification-download-added-to-the-queue',
            'folder',
            result.file.name
          );
          this.displayToastMessage(toastMessage, ToastTypeIndex.info);
          const key = result.location.concat(result.file.lastModified.toString());
          this.statusCenterData.set(key, result);
        } else {
          console.error('Error: There is some error in getting compressed file progress info. Please try again.');
        }
      }
    );

    this.itemCompressAndDownloadTriggerSubscription = this.globalEventBusService.register(
      GlobalEvents.ItemCompressAndDownloadTriggered,
      (result: IFileActionProgress) => {
        if (result) {
          const key = result.location.concat(result.file.name).concat(result.file.lastModified.toString());
          if (this.statusCenterData.get(key) && result.contentStatus === ContentStatus.InProgress) {
            this.statusCenterData.get(key).progress = result.progress;
            this.statusCenterData.get(key).actionType = result.actionType;
            this.statusCenterData.get(key).location = result.location;
          } else {
            this.statusCenterData.delete(key);
            this.statusCenterData.set(key, result);
          }
        } else {
          console.error(
            'Error: There is some error in getting download file progress info of compressed file. Please try again.'
          );
        }
      }
    );
  }

  private get deviceOsType(): number {
    return RcContext.instance.deviceContext?.osType || OsTypes.Unknown;
  }

  ngOnDestroy() {
    if (this.fileUploadProgressSubscription) {
      this.fileUploadProgressSubscription.unsubscribe();
    }
    if (this.fileDownloadProgressSubscription) {
      this.fileDownloadProgressSubscription.unsubscribe();
    }
    if (this.fileDownloadTriggerSubscription) {
      this.fileDownloadTriggerSubscription.unsubscribe();
    }
    if (this.itemCompressTriggerSubscription) {
      this.itemCompressTriggerSubscription.unsubscribe();
    }
    if (this.itemCompressAndDownloadTriggerSubscription) {
      this.itemCompressAndDownloadTriggerSubscription.unsubscribe();
    }

    this.onDestroy.next();
    this.onDestroy.complete();
  }

  public updateStatusCenter = (key: string, newStatus: ContentStatus) => {
    const data = this.statusCenterData.get(key);
    if (data) {
      data.contentStatus = newStatus;
    }
  };

  /**
   *  Transfers the state FileManager of another instance to the current one
   *  This transfer function runs on the receiver side of a transfer.
   *  The first parameter is the instance of the service hosted on the sender side of the transfer.
   */
  public transfer = (fileManagerService: FileManagerService, mcDeviceId: string) => {
    const oldState = fileManagerService.getState(mcDeviceId);
    const newState = this.getState(mcDeviceId);
    this.statusCenterData = fileManagerService.getStatusCenterData();
    this.isFieldIndicatorActive = fileManagerService.isFieldIndicatorActive;
    newState.transfer(oldState);
  };

  /** Returns whether file upload or download is under process */
  public get isCommandInProgress() {
    return this.commandInProgress;
  }

  /** Sets the state of file upload or download is under process */
  public set isCommandInProgress(value: boolean) {
    this.commandInProgress = value;
  }

  /** Gets the state of the FileManager instance */
  public getState = (mcOrSaDeviceId: string): FileManagerState => {
    if (mcOrSaDeviceId === undefined) {
      return undefined;
    }
    const keys = Object.keys(this.states);
    for (const key of keys) {
      if (this.deviceIdComparerService.areDeviceIdsEqual(key, mcOrSaDeviceId)) {
        return this.states[key];
      }
    }
    let state: FileManagerState;

    state = new FileManagerState(
      RcContext.instance,
      this.saI18nPipe,
      this.dateFormatPipe,
      this.featureToggleService,
      this.globalEventBusService,
      this.fileSystem
    );
    state.init();
    this.states[mcOrSaDeviceId] = state;

    return state;
  };

  /** Resets the state of the FileManager instance */
  public resetState = (mcOrSaDeviceId: string) => {
    const keys = Object.keys(this.states);
    let keyToDelete = '';
    for (const key of keys) {
      if (this.deviceIdComparerService.areDeviceIdsEqual(key, mcOrSaDeviceId)) {
        keyToDelete = key;
        break;
      }
    }

    if (keyToDelete) {
      delete this.states[keyToDelete];
    }

    // reset file upload/download queue state
    this.clearFileState();
    this.commandsQueue.splice(0, this.commandsQueue.length);
  };

  /** Emits the RC session ended notification */
  public endSession = (): void => {
    this.sessionEnded.next();
  };

  /** Clears all the elements from the StatusCenter Data Map */
  public clearStatusCenterData = (): void => {
    this.statusCenterData.clear();
  };

  /** Returns all the elements from the StatusCenter Data Map */
  public getStatusCenterData = (): Map<Object, IFileActionProgress> => this.statusCenterData || null;

  /**Returns if a download  or upload is currently in progress */
  public fileTransferInProgress = (): boolean => {
    if (!this.statusCenterData) {
      return false;
    }
    const filesUnderDownloadProcess = Array.from(this.statusCenterData.values()).filter(
      x => x.contentStatus === ContentStatus.InProgress || x.contentStatus === ContentStatus.CompressInProgress
    );

    return filesUnderDownloadProcess.length > 0;
  };

  /** Clears all the elements from the StatusCenter Data Map based on ContentStatus */
  public clearStatusCenterDataByContentStatus = (contentStatus: ContentStatus[]): void => {
    this.getStatusCenterData().forEach((value, key) => {
      if (contentStatus.includes(value.contentStatus)) {
        this.getStatusCenterData().delete(key);
      }
    });
  };

  /** Delete single element from the StatusCenter Data Map based on status data*/
  public deleteSingleRecordFromStatusCenter = (statusData: IFileActionProgress): void => {
    this.getStatusCenterData().forEach((value, key) => {
      if (value === statusData) {
        this.getStatusCenterData().delete(key);
      }
    });
  };

  public getCurrentItemType = (): string => {
    const state: FileManagerState = this.getState(RcContext.instance.sotiAssistDeviceId);
    if (!state.selectedTreeNodes || state.selectedTreeNodes.length === 0) {
      throw 'Can be called only if at least one selected node';
    }
    return state.selectedTreeNodes[0].type as string;
  };

  public getCurrentItem = (): FileData => {
    const state: FileManagerState = this.getState(RcContext.instance.sotiAssistDeviceId);
    if (!state.selectedTreeNodes || state.selectedTreeNodes.length === 0) {
      throw 'Can be called only if at least one selected node';
    }
    return state.selectedTreeNodes[0].data as FileData;
  };

  public downloadSelectedItem = () => {
    const state: FileManagerState = this.getState(RcContext.instance.deviceId);
    if (state && state.selectedTreeNodes && state.selectedTreeNodes.length > 0) {
      state.download(state.selectedTreeNodes[0]);
    }
  };

  public compressSelectedItem = (itemName: IFileManagerActionItem, download: boolean = true) => {
    const state: FileManagerState = this.getState(RcContext.instance.deviceId);
    if (state && state.selectedTreeNodes && state.selectedTreeNodes.length > 0) {
      const key = itemName.filePath.concat(itemName.file.lastModified.toString());
      this.updateStatusCenter(key, ContentStatus.CompressInProgress);

      const compressRequestId = this.fileSystem.compressItem(itemName.filePath, (filePath, error) => {
        if (this.featureToggleService.isEnabled('DeveloperMode')) {
          console.log('PC_DOS_COMPRESSION complete');
        }

        if (error != AgentErrorCode.Success) {
          this.statusCenterData.delete(key);
          if (error != AgentErrorCode.OperationCancelled) {
            this.updateFileErrorInStatusCenter(
              this.createFile(itemName.fileName),
              ActionType.Download,
              this.getErrorDescription(error, ActionType.Download),
              itemName.filePath
            );
          }
          this.processNextFileManagerCommand();
        } else if (download) {
          // trigger file download
          const fileInfo: IFileInfo = {
            //extract the file name from a full path
            fileName: filePath.replace(/^.*[\\\/]/, ''),
            filePath,
          };
          this.zipFilesQueue.push(fileInfo.filePath);
          this.downloadFile(fileInfo.filePath, itemName.file, true);
        } else {
          state.exploreTo(state.selectedTreeNodes[0].parent);
        }
      });
      this.statusCenterData.get(key).compressRequestId = compressRequestId;
    }
  };

  public renameItem = (oldFullName: string, newFullName: string, newName: string): Observable<AgentErrorCode> => {
    const result = new Observable<AgentErrorCode>(subscriber => {
      this.fileSystem.renameItem(oldFullName, newFullName, result => {
        const state: FileManagerState = this.getState(RcContext.instance.sotiAssistDeviceId);
        state.renameTo(state.selectedTreeNodes[0], newName);

        subscriber.next(result);
        subscriber.complete();
      });
    });

    return result;
  };

  /** Download multiple files to device a folder file path */
  public downloadContent = (fileName: string, filePath: string) => {
    if (fileName.length == 0 || filePath.length == 0) {
      return;
    }

    // queue content
    const fileManagerQueueItem: IFileManagerActionItem = {
      actionType: ActionType.Download,
      fileName,
      filePath,
      file: this.createFile(fileName),
      content: null,
    };

    this.commandsQueue.push(fileManagerQueueItem);
    this.addCommandsToStatusCenter(fileManagerQueueItem);

    if (!this.isCommandInProgress) {
      this.processNextFileManagerCommand();
    }
  };

  public addSelectedItemCompressionToTheQueue = () => {
    const state: FileManagerState = this.getState(RcContext.instance.deviceId);
    if (state && state.selectedTreeNodes && state.selectedTreeNodes.length > 0) {
      const filePath = state.selectedTreeNodes[0].data.fullPath;
      const fileName = state.selectedTreeNodes[0].data.fileName;

      // queue content
      const fileManagerQueueItem: IFileManagerActionItem = {
        actionType: ActionType.Compress,
        fileName,
        filePath,
        file: this.createFile(fileName),
        content: null,
      };

      this.commandsQueue.push(fileManagerQueueItem);
      this.addCommandsToStatusCenter(fileManagerQueueItem);

      if (!this.isCommandInProgress) {
        this.processNextFileManagerCommand();
      }
    }
  };

  /** Upload multiple files to device folder file path */
  public uploadContent = (content: UploadContent[], filePath: string) => {
    if (content == null || content.length == 0) {
      return;
    }

    // show a toast message
    const toastMessage = this.saI18nPipe.transform('file-manager-status-center-notification-upload-added-to-the-queue');
    this.displayToastMessage(toastMessage, ToastTypeIndex.info);

    // queue content
    this.deviceKind = RcContext.instance.deviceKind;
    const fileManagerQueueItem: IFileManagerActionItem = {
      actionType: ActionType.Upload,
      content,
      filePath,
      fileName: null,
    };

    this.commandsQueue.push(fileManagerQueueItem);
    this.addCommandsToStatusCenter(fileManagerQueueItem);

    if (!this.isCommandInProgress) {
      this.processNextFileManagerCommand();
    }
  };

  /** check if file exists in current path */
  public isFileExistsOnRemoteFolder = (fullFileName: string, fileName: string): Observable<boolean> =>
    new Observable<boolean>(subscriber => {
      this.fileSystem.getFolderContents(fullFileName + '/*', data => {
        subscriber.next(data?.tree.some(fileItem => fileItem.title.toLowerCase() == fileName.toLowerCase()));
        subscriber.complete();
      });
    });

  /** Stops upload or download for all the remaining files */
  public cancelEntireFileAction = () => {
    /** Clear the queues for pending files */
    if (this.uploadFileList && this.uploadFileList.length > 0) {
      this.uploadFileList = [];
    }
    if (this.commandsQueue && this.commandsQueue.length > 0) {
      this.commandsQueue = [];
    }

    if (this.isCommandInProgress) {
      this.isRcSessionClosing = true;
      const state: FileManagerState = this.getState(RcContext.instance.deviceId);
      state.stopUpload();
      state.stopDownload();
      Array.from(this.statusCenterData.values()).forEach(x => {
        if (x.compressRequestId) {
          state.stopCompress(x.compressRequestId);
        }
      });
    }
  };

  /** Stops upload or download */
  public cancelFileAction = (statusData: IFileActionProgress) => {
    let description = '';
    if (statusData.actionType === ActionType.Upload) {
      this.stopUpload(statusData);
      description = `${this.saI18nPipe.transform('file-manager-status-center-errormessge-upload-cancelled')}`;
    }

    if (statusData.actionType === ActionType.Download) {
      this.stopDownload(statusData);
      description = `${this.saI18nPipe.transform('file-manager-status-center-errormessge-download-cancelled')}`;
    }

    if (statusData.actionType === ActionType.Compress) {
      this.stopCompress(statusData);
      description = `${this.saI18nPipe.transform('file-manager-status-center-errormessge-download-cancelled')}`;
    }

    this.updateFileProgressInStatusCenter(
      statusData.file,
      statusData.actionType,
      this.progressDone,
      statusData.location,
      false,
      description,
      true
    );
  };

  private createFile(fileName): File {
    return new File([fileName], fileName);
  }

  private stopUpload(statusData: IFileActionProgress) {
    if (statusData.progress > 0) {
      const state: FileManagerState = this.getState(RcContext.instance.deviceId);
      state.stopUpload();
    } else {
      const uploadFile = this.uploadFileList.find(i => i.content === statusData.file);
      if (uploadFile) {
        this.uploadFileList.splice(this.uploadFileList.indexOf(uploadFile), 1);
      } else if (this.commandsQueue) {
        for (const uploadCommand of this.commandsQueue.filter(i => i.actionType === ActionType.Upload)) {
          const uploadCommandFile = uploadCommand.content.find(i => i.content === statusData.file);
          if (uploadCommandFile) {
            uploadCommand.content.splice(uploadCommand.content.indexOf(uploadCommandFile), 1);

            if (uploadCommand.content.length === 0) {
              this.commandsQueue.splice(this.commandsQueue.indexOf(uploadCommand), 1);
            }
          }
        }
      }
    }
  }

  private stopDownload(statusData: IFileActionProgress) {
    if (statusData.progress > 0) {
      const state: FileManagerState = this.getState(RcContext.instance.deviceId);
      state.stopDownload();
    } else if (this.commandsQueue) {
      for (const uploadCommand of this.commandsQueue.filter(i => i.actionType === ActionType.Download)) {
        let statusDataPath = statusData.location + statusData.file.name;
        statusDataPath = statusDataPath.replace(/\\/g, '/');

        if (uploadCommand.filePath === statusDataPath) {
          this.commandsQueue.splice(this.commandsQueue.indexOf(uploadCommand), 1);
        }
      }
    }
  }

  private stopCompress(statusData: IFileActionProgress) {
    const state: FileManagerState = this.getState(RcContext.instance.deviceId);
    if (state && statusData.actionType === ActionType.Compress) {
      const compressRequestIdToBeDeleted = statusData.compressRequestId;
      // Getting the compressRequest Id for the queue action item for which cancel button has been clicked.
      if (compressRequestIdToBeDeleted) {
        state.stopCompress(compressRequestIdToBeDeleted);
        if (this.featureToggleService.isEnabled('DeveloperMode')) {
          console.log('Cancel Compression message sent');
        }
      }
      // This will remove that clicked entry from the queue and statusCenterData map for which no compression has been initiated yet.
      else {
        const itemToBeRemovedFromQueue = this.commandsQueue.find(x => x.file === statusData.file);
        this.commandsQueue.splice(this.commandsQueue.indexOf(itemToBeRemovedFromQueue), 1);
        const key = statusData.location.concat(statusData.file.lastModified.toString());
        this.statusCenterData.delete(key);
      }
    }
  }

  private processNextFileManagerCommand = () => {
    if (this.queuePaused) {
      return;
    }
    this.clearFileState();
    const queueActionItem = this.commandsQueue.shift();
    if (queueActionItem) {
      this.isCommandInProgress = true;
      if (queueActionItem.actionType == ActionType.Upload) {
        this.uploadFilePath = queueActionItem.filePath;
        this.uploadFileList = queueActionItem.content;

        this.uploadNextFileIfNeeded();
      } else if (queueActionItem.actionType == ActionType.Download) {
        this.downloadFile(queueActionItem.filePath, queueActionItem.file);
      } else if (queueActionItem.actionType == ActionType.Compress) {
        this.compressSelectedItem(queueActionItem);
      }
    } else {
      this.isCommandInProgress = false;
      this.deviceKind = null;
    }
  };

  /** pauses processing of next item in status queue */
  public pauseQueue = () => {
    this.queuePaused = true;
  };

  /** unpause queue and kick of processing of next item */
  public resumeQueue = () => {
    if (this.queuePaused) {
      this.queuePaused = false;

      //if a command is not currently being processed start next command
      if (!this.isCommandInProgress) {
        this.processNextFileManagerCommand();
      }
    }
  };

  public deleteFolder = (fileName: string): Observable<AgentErrorCode> => {
    const osType = this.deviceOsType;

    // Android Agent will just simply call java file delete
    // https://docs.oracle.com/javase/7/docs/api/java/io/File.html#delete()

    if (OsDetectionService.IsAndroid(osType)) {
      const observable = new Observable<AgentErrorCode>(subscriber => {
        // PC_DOS_EMPTY_FOLDER will recursively remove subfolder's contents
        this.fileSystem.emptyFolder(fileName, resultContent => {
          // Initialte the next filesystem call after first subscriber complete

          console.log('PC_DOS_EMPTY_FOLDER complete');

          if (resultContent > 0) {
            const state: FileManagerState = this.getState(RcContext.instance.sotiAssistDeviceId);
            state.deleteNode(state.selectedTreeNodes[0]);

            subscriber.next(resultContent);
            subscriber.complete();
          } else {
            this.fileSystem.deleteFolder(fileName, resultParent => {
              console.log('PC_DOS_RMDIR complete');
              const state: FileManagerState = this.getState(RcContext.instance.sotiAssistDeviceId);
              state.deleteNode(state.selectedTreeNodes[0]);

              subscriber.next(resultParent);
              subscriber.complete();
            });
          }
        });
      });

      return observable;
    } else if (
      OsDetectionService.IsWindowsCe(osType) ||
      OsDetectionService.IsUniversalWindows(osType) ||
      OsDetectionService.IsWindows(osType)
    ) {
      const observable = new Observable<AgentErrorCode>(subscriber => {
        this.fileSystem.emptyFolder(fileName, result => {
          const state: FileManagerState = this.getState(RcContext.instance.sotiAssistDeviceId);
          state.deleteNode(state.selectedTreeNodes[0]);

          subscriber.next(result);
          subscriber.complete();
        });
      });

      return observable;
    }
    // Unix will Delete a Folder in Bash using the rm command with the recursive option, -r.
    // Remove a directory and all its contents, including any subdirectories and files
    else {
      const result = new Observable<AgentErrorCode>(subscriber => {
        this.fileSystem.deleteFolder(fileName, result => {
          const state: FileManagerState = this.getState(RcContext.instance.sotiAssistDeviceId);
          state.deleteNode(state.selectedTreeNodes[0]);

          subscriber.next(result);
          subscriber.complete();
        });
      });

      return result;
    }
  };

  public deleteFile = (fileName: string): Observable<AgentErrorCode> => {
    const result = new Observable<AgentErrorCode>(subscriber => {
      this.fileSystem.deleteFile(fileName, result => {
        const state: FileManagerState = this.getState(RcContext.instance.sotiAssistDeviceId);
        if (state) {
          const nodeToDelete =
            state.selectedTreeNodes.length > 0 ? state.selectedTreeNodes[0] : state.findTreeNode(fileName);

          if (nodeToDelete) {
            state.deleteNode(nodeToDelete);
          }
        }

        subscriber.next(result);
        subscriber.complete();
      });
    });

    return result;
  };

  // Available on Android only
  // Agent 14.5.4 and up
  public isCompressItemSupported = (): boolean => {
    return OsDetectionService.IsAndroid(this.deviceOsType) &&
      (RcContext.instance.versionHi > 14 || (RcContext.instance.versionHi === 14 && RcContext.instance.versionLo >= 54));
  };

  private uploadNextFile = (): void => {
    if (this.filePathHasDirectory()) {
      this.createFileDirectoryPath();
    } else {
      this.uploadFile(this.currentUploadFile, this.uploadFilePath);
    }
  };

  private uploadNextFileIfNeeded = (): void => {
    this.currentUploadFile = this.uploadFileList.length > 0 ? this.uploadFileList.shift() : null;
    if (this.currentUploadFile) {
      this.uploadNextFile();
    } else {
      this.processNextFileManagerCommand();
    }
  };

  private uploadFile = (uploadContent: UploadContent, filePath: string): void => {
    const file = uploadContent.content;
    if (file.size <= 0) {
      // ignoring 0 bytes file
      this.updateFileErrorInStatusCenter(
        file,
        ActionType.Upload,
        `${this.saI18nPipe.transform('file-manager-status-center-errormessage-zerobytes-file')}`,
        filePath,
        ContentStatus.Failed
      );
      this.uploadNextFileIfNeeded();
      return;
    }

    if (uploadContent.overwrite) {
      this.sendFile(uploadContent.content, filePath);
      return;
    }

    this.isFileExistsOnRemoteFolder(filePath, file.name).subscribe(exist => {
      if (exist) {
        this.updateFileErrorInStatusCenter(
          file,
          ActionType.Upload,
          `${this.saI18nPipe.transform('file-manager-status-center-errormessge-alreadyexist')}`,
          filePath,
          ContentStatus.AwaitFeedback
        );
        this.uploadNextFileIfNeeded();
      } else {
        this.sendFile(uploadContent.content, filePath);
      }
    });
  };

  private sendFile = (file: File, filePath: string): void => {
    this.fileSystem.sendFileInfo(file.size, filePath + file.name, (errorCode: number) => {
      if (errorCode == AgentErrorCode.Success) {
        this.fileSystem.sendFileContents(
          file,
          errorCode => {
            // error callback
            if (errorCode === FileSystemErrorCodes.RCPauseInterruptedFileAction) {
              // when RC gets paused on Agent side; current upload action fails;
              // move card to error queue and pause any further queued file actions
              this.updateFileErrorInStatusCenter(
                file,
                ActionType.Upload,
                this.getErrorDescription(errorCode, ActionType.Upload),
                filePath
              );
              this.errorCurrentUploadItems(errorCode, filePath);
              this.errorQueuedFileActions(errorCode);

              // clear queue state
              this.clearFileState();
              this.commandsQueue.splice(0, this.commandsQueue.length);
              this.commandsQueue = [];
              this.commandInProgress = false;
            } else if (errorCode === AgentErrorCode.Client_Upload_Cancelled_Request) {
              this.deleteFile(filePath + file.name)
                .pipe(takeUntil(this.onDestroy))
                .subscribe(result => {
                  this.uploadNextFileIfNeeded();
                  if (this.isRcSessionClosing) {
                    this.fileActionCancelled.next();
                    this.isRcSessionClosing = false;
                  }
                });
            } else {
              this.updateFileErrorInStatusCenter(
                file,
                ActionType.Upload,
                this.getErrorDescription(errorCode, ActionType.Upload),
                filePath
              );
              this.uploadNextFileIfNeeded();
            }
          },
          progress => {
            // Update file progress in Status Center UI
            this.updateFileProgressInStatusCenter(file, ActionType.Upload, progress, filePath);
          },
          () => {
            // file upload complete callback
            // refresh file info in file manager
            this.displaySuccessToastMessage('file-manager-status-center-notification-upload-success', file.name);
            const state: FileManagerState = this.getState(RcContext.instance.deviceId);
            if (state) {
              state.refreshActiveFolder(filePath, this.uploadFilePath);
            }
            this.uploadNextFileIfNeeded();
          }
        );
      } else {
        this.updateFileErrorInStatusCenter(
          file,
          ActionType.Upload,
          this.getErrorDescription(errorCode, ActionType.Upload),
          filePath
        );
        this.uploadNextFileIfNeeded();
      }
    });
  };

  private getDirectoryPath = (filePath: string) => {
    let directoryPath: string;
    const osType = this.deviceOsType;
    if (OsDetectionService.IsWindowsCe(osType) || OsDetectionService.IsWindows(osType)) {
      directoryPath = filePath.substring(0, filePath.lastIndexOf('/') + 1).replace(/\//g, '\\');
    } else {
      directoryPath = filePath.substring(0, filePath.lastIndexOf('/') + 1);
    }
    return directoryPath;
  };

  private errorCurrentUploadItems(errorCode: number, uploadFilePath: string) {
    if (this.uploadFileList.length > 0) {
      this.uploadFileList.forEach(uploadFile => {
        this.updateFileErrorInStatusCenter(
          uploadFile.content,
          ActionType.Upload,
          this.getErrorDescription(errorCode, ActionType.Download),
          uploadFilePath
        );
      });
    }
  }

  private errorQueuedFileActions(errorCode: number) {
    if (this.commandsQueue.length > 0) {
      this.commandsQueue.forEach(queuedItem => {
        if (queuedItem.actionType == ActionType.Upload) {
          queuedItem.content.forEach(fileContent => {
            this.updateFileErrorInStatusCenter(
              fileContent.content,
              queuedItem.actionType,
              this.getErrorDescription(errorCode, queuedItem.actionType),
              queuedItem.filePath
            );
          });
        } else if (queuedItem.actionType == ActionType.Download) {
          const directoryPath = this.getDirectoryPath(queuedItem.filePath);
          this.updateFileErrorInStatusCenter(
            queuedItem.file,
            queuedItem.actionType,
            this.getErrorDescription(errorCode, queuedItem.actionType),
            directoryPath
          );
        }
      });
    }
  }

  // isCompressedFileDownloading parameter represents if we are downloading a compressed file, since there is a separate event being fired. i.e. ItemCompressAndDownloadTriggered
  private downloadFile = (filePath: string, file: File, isCompressedFileDownloading = false) => {
    const directoryPath = this.getDirectoryPath(filePath);
    this.fileSystem.getFileContents(
      file.name,
      filePath,
      (data: Uint8Array, error: number) => {
        if (data !== undefined) {
          const blob = new Blob([data.buffer], { type: 'application/x-object' });
          try {
            this.updateFileProgressInStatusCenter(
              file,
              ActionType.Download,
              this.progressDone,
              directoryPath,
              isCompressedFileDownloading
            );
            if (isCompressedFileDownloading) {
              this.downloadService.triggerDownload(blob, filePath.replace(/^.*[\\\/]/, ''));
            } else {
              this.downloadService.triggerDownload(blob, file.name);
            }
            this.displaySuccessToastMessage('file-manager-status-center-notification-download-success', file.name);
            this.processNextFileManagerCommand();
            this.clearZipFilesQueue(filePath);
          } catch (e) {
            this.updateFileErrorInStatusCenter(file, ActionType.Download, this.saI18nPipe.transform(e), directoryPath);
            this.processNextFileManagerCommand();
            this.clearZipFilesQueue(filePath);
          }
        } else if (error === FileSystemErrorCodes.RCPauseInterruptedFileAction) {
          // when RC gets paused on Agent side; current download action fails;
          // move card to error queue and error out any further queued file actions
          this.updateFileErrorInStatusCenter(
            file,
            ActionType.Download,
            this.getErrorDescription(error, ActionType.Download),
            directoryPath
          );
          this.errorQueuedFileActions(error);

          // clear queue state
          this.clearFileState();
          this.commandsQueue.splice(0, this.commandsQueue.length);
          this.commandsQueue = [];
          this.commandInProgress = false;
          return;
        } else if (
          error === FileSystemErrorCodes.FileMemoryAllocation ||
          error === FileSystemErrorCodes.FatalError
        ) {
          this.updateFileErrorInStatusCenter(
            file,
            ActionType.Download,
            this.getErrorDescription(error, ActionType.Download),
            directoryPath
          );
          this.processNextFileManagerCommand();
        } else if (error === AgentErrorCode.Client_Download_Cancelled_Request) {
          this.clearZipFilesQueue(filePath);
          if (this.isRcSessionClosing) {
            this.fileActionCancelled.next();
            this.isRcSessionClosing = false;
          }
          this.processNextFileManagerCommand();
        } else {
          let errorText = getWin32ErrorMessage(error);
          this.clearZipFilesQueue(filePath);
          this.updateFileErrorInStatusCenter(file, ActionType.Download, errorText, directoryPath);
          this.processNextFileManagerCommand();
        }
      },
      (path, completed, total) => {
        if (this.featureToggleService.isEnabled('DeveloperMode')) {
          console.log('Downloading ' + file.name + ' Progress - ' + Math.round((completed / total) * 100) + '%');
        }

        // Update file progress in Status Center UI
        let progress = Math.round((completed / total) * 100);
        progress = progress > 99 ? 99 : progress;
        this.updateFileProgressInStatusCenter(
          file,
          ActionType.Download,
          progress,
          directoryPath,
          isCompressedFileDownloading
        );
      }
    );
  };

  private displayToastMessage = (message: string, type: ToastTypeIndex) => {
    this.globalEventBusService.broadcast(GlobalEvents.showToastMessage, { message, type } as Toast);
  };

  // Raise event to display success toast message.
  private displaySuccessToastMessage = (messageKey: string, fileName: string) => {
    this.globalEventBusService.broadcast(GlobalEvents.showToastMessage, {
      message: this.saI18nPipe.transform(messageKey, fileName),
      type: ToastTypeIndex.success,
    } as Toast);
  };

  // Raise event to display error toast message.
  private displayErrorToastMessage = (actionType: ActionType, fileName: string, errorDescription: string) => {
    const messageKey =
      actionType == ActionType.Upload
        ? 'file-manager-status-center-notification-upload-failed'
        : 'file-manager-status-center-notification-download-failed';

    const errorMessage = `${this.saI18nPipe.transform(messageKey, fileName)} ${errorDescription}`;
    this.isFieldIndicatorActive = true;
    this.globalEventBusService.broadcast(GlobalEvents.showToastMessage, {
      message: errorMessage,
      type: ToastTypeIndex.error,
    } as Toast);
  };

  private addCommandsToStatusCenter = (fileManagerQueueItem: IFileManagerActionItem): void => {
    if (fileManagerQueueItem.actionType == ActionType.Download) {
      const directoryPath = this.getDirectoryPath(fileManagerQueueItem.filePath);
      const downloadContentProgressInfo: IFileActionProgress = {
        file: fileManagerQueueItem.file,
        actionType: ActionType.Download,
        progress: 0,
        location: directoryPath,
        contentStatus: ContentStatus.InProgress,
        timeOfCompletion: new Date(),
      };
      this.globalEventBusService.broadcast(GlobalEvents.fileDownloadUpdated, downloadContentProgressInfo);
    } else if (fileManagerQueueItem.actionType == ActionType.Upload) {
      fileManagerQueueItem.content.forEach(x => {
        const uploadContentProgressInfo: IFileActionProgress = {
          file: x.content,
          actionType: ActionType.Upload,
          progress: 0,
          location: this.updateFilePathBasedOnDeviceKind(fileManagerQueueItem.filePath, this.deviceKind, x),
          contentStatus: ContentStatus.InProgress,
        };
        this.globalEventBusService.broadcast(GlobalEvents.fileUploadUpdated, uploadContentProgressInfo);
      });
    } else if (fileManagerQueueItem.actionType == ActionType.Compress) {
      const itemCompressProgressInfo: IFileActionProgress = {
        file: fileManagerQueueItem.file,
        actionType: ActionType.Compress,
        contentStatus: ContentStatus.InProgress,
        location: fileManagerQueueItem.filePath,
        timeOfCompletion: new Date(),
      };
      this.globalEventBusService.broadcast(GlobalEvents.ItemCompressTriggered, itemCompressProgressInfo);
    }
  };

  private updateFilePathBasedOnDeviceKind = (
    filePath: string,
    deviceKind: string,
    uploadContent: UploadContent
  ): string => {
    if (deviceKind.startsWith('Windows')) {
      return filePath
        .concat(uploadContent.content['webkitRelativePath'].replace(/\//g, '\\'))
        .replace(uploadContent.content.name, '');
    } else {
      return filePath.concat(uploadContent.content['webkitRelativePath']).replace(uploadContent.content.name, '');
    }
  };

  private updateFileProgressInStatusCenter = (
    file: File,
    actionType: ActionType,
    progress: number,
    filePath: string,
    isCompressedFileDownloading: boolean = false,
    errorOrDescription: string = '',
    isCancellation: boolean = false
  ): void => {
    if (this.featureToggleService.isEnabled('DeveloperMode')) {
      console.log('Uploading ' + file.name + ' Progress - ' + progress + '%');
    }

    const fileProgressInfo: IFileActionProgress = {
      file,
      actionType,
      progress,
      location: filePath,
      error: errorOrDescription,
      isCancellation,
      ...(progress < this.progressDone
        ? { contentStatus: ContentStatus.InProgress }
        : { contentStatus: ContentStatus.Done }),
    };

    if (progress == this.progressDone) {
      fileProgressInfo.timeOfCompletion = new Date();
    }

    let eventKey = '';
    if (isCompressedFileDownloading) {
      eventKey = GlobalEvents.ItemCompressAndDownloadTriggered;
    } else {
      eventKey = actionType == ActionType.Upload ? GlobalEvents.fileUploadUpdated : GlobalEvents.fileDownloadUpdated;
    }
    this.globalEventBusService.broadcast(eventKey, fileProgressInfo);
  };

  private clearZipFilesQueue(fileFullPath: string) {
    const zipFile = this.zipFilesQueue.find(i => i === fileFullPath);
    if (zipFile) {
      this.zipFilesQueue.splice(this.zipFilesQueue.indexOf(zipFile), 1);
      this.deleteFile(zipFile)
        .pipe(takeUntil(this.onDestroy))
        .subscribe(result => {
          if (this.featureToggleService.isEnabled('DeveloperMode')) {
            console.log('Zip file deleted: ' + zipFile);
          }
        });
    }
  }

  private updateFileErrorInStatusCenter = (
    file: File,
    actionType: ActionType,
    errorDescription: string,
    filePath: string,
    contentStatus?: ContentStatus,
    showToastMessage: boolean = true
  ): void => {
    const fileProgressInfo: IFileActionProgress = {
      file,
      actionType,
      contentStatus: contentStatus ? contentStatus : ContentStatus.Failed,
      error: errorDescription,
      location: filePath,
      timeOfCompletion: new Date(),
    };

    if (showToastMessage) {
      this.displayErrorToastMessage(actionType, file.name, errorDescription);
    }

    const eventKey =
      actionType == ActionType.Upload ? GlobalEvents.fileUploadUpdated : GlobalEvents.fileDownloadUpdated;
    this.globalEventBusService.broadcast(eventKey, fileProgressInfo);
  };

  private clearFileState = (): void => {
    this.uploadFileList = [];
    this.currentUploadFile = null;
    this.uploadFilePath = null;
    this.uploadDirectoryPath = null;
    this.uploadDirectoryIndex = -1;
  };

  private filePathHasDirectory = (): boolean => {
    const filePath = this.currentUploadFile.path;
    return filePath.split('/').length > 1;
  };

  private createFileDirectoryPath = (): void => {
    this.uploadDirectoryIndex = 0;
    this.uploadDirectoryPath = this.uploadFilePath;
    this.createNextDirectoryPath();
  };

  private createNextDirectoryPath() {
    const filePath = this.currentUploadFile.path;
    const dirTokens = filePath.split('/');
    const fileIndex = dirTokens.length - 1;

    const directoryDelimiter = this.deviceKind.startsWith('Windows') ? '\\' : '/';
    if (!this.uploadDirectoryPath.endsWith(directoryDelimiter)) {
      this.uploadDirectoryPath += directoryDelimiter;
    }

    if (this.uploadDirectoryIndex == fileIndex) {
      this.uploadFile(this.currentUploadFile, this.uploadDirectoryPath);
    } else if (this.uploadDirectoryIndex < fileIndex) {
      const newDirectory = dirTokens[this.uploadDirectoryIndex];
      this.uploadDirectoryPath = this.uploadDirectoryPath + newDirectory;
      this.fileSystem.createDirectory(this.uploadDirectoryPath, (errorCode: number) => {
        if (errorCode == AgentErrorCode.Success || errorCode == AgentErrorCode.Already_Exists) {
          this.uploadDirectoryIndex++;
          this.createNextDirectoryPath();
        } else {
          this.updateFileErrorInStatusCenter(
            this.currentUploadFile.content,
            ActionType.Upload,
            this.getErrorDescription(errorCode, ActionType.Upload),
            this.uploadDirectoryPath
          );
          this.uploadNextFileIfNeeded();
        }
      });
    }
  }

  private getErrorDescription = (errorCode: number, actionType: ActionType): string => {
    let errorDescription;
    switch (errorCode) {
      case AgentErrorCode.Disk_Full:
        if (actionType === ActionType.Upload) {
          errorDescription = `${this.saI18nPipe.transform('file-manager-status-center-upload-errormessge-diskfull')}`;
        } else if (actionType === ActionType.Download) {
          errorDescription = `${this.saI18nPipe.transform('file-manager-status-center-download-errormessge-diskfull')}`;
        }
        break;
      case AgentErrorCode.Access_Denied:
        errorDescription = `${this.saI18nPipe.transform('file-manager-status-center-errormessge-accessdenied')}`;
        break;
      case AgentErrorCode.Invalid_Param:
        errorDescription = `${this.saI18nPipe.transform('file-manager-status-center-errormessge-invalidparam')}`;
        break;
      case AgentErrorCode.Folder_Empty:
        errorDescription = `${this.saI18nPipe.transform('file-manager-status-center-errormessge-emptyfolder')}`;
        break;
      case FileSystemErrorCodes.FileMemoryAllocation:
        errorDescription = `${this.saI18nPipe.transform(
          'file-manager-status-center-errormessge-downloadfile-memoryallocation'
        )}`;
        break;
      case AgentErrorCode.OperationCancelled:
        switch (actionType) {
          case ActionType.Upload:
            errorDescription = `${this.saI18nPipe.transform(
              'file-manager-status-center-errormessge-upload-cancelled'
            )}`;
            break;
          case ActionType.Download:
          case ActionType.Compress:
            errorDescription = `${this.saI18nPipe.transform(
              'file-manager-status-center-errormessge-download-cancelled'
            )}`;
            break;
        }
        break;

      default:
        errorDescription = `${this.saI18nPipe.transform('file-manager-status-center-errormessge-erroroccured')}`;
    }

    return errorDescription;
  };
}
